import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PARTNERS } from "@/data/partners";

const PartnerItem = ({ p }) => {
  if (p.image) {
    return (
      <a
        href={p.href}
        target="_blank"
        rel="noopener noreferrer"
        className="group shrink-0 px-12 py-3 flex items-center justify-center h-20"
        data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
        title={p.name}
      >
        <img
          src={p.image}
          alt={p.name}
          className="max-h-12 w-auto max-w-[170px] object-contain transition-all duration-500 grayscale-0 group-hover:[filter:grayscale(1)_brightness(0.6)_sepia(1)_saturate(8)_hue-rotate(-25deg)]"
          loading="lazy"
        />
      </a>
    );
  }
  return (
    <a
      href={p.href}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 px-12 py-3 font-display font-semibold text-[1.5rem] tracking-tight text-ink/40 hover:text-ink transition-colors duration-500 whitespace-nowrap"
      data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {p.text}
    </a>
  );
};

const AUTO_SCROLL_PX_PER_TICK = 1;
const AUTO_SCROLL_INTERVAL_MS = 24; // ~42 fps → smooth, ~30s full loop
const RESUME_AFTER_MS = 2500;
const ARROW_STEP_PX = 320;

const PartnersMarquee = ({ heading = "Trusted by" }) => {
  // duplicate three times so wrap-around feels seamless on wide screens
  const items = [...PARTNERS, ...PARTNERS, ...PARTNERS];
  const trackRef = useRef(null);
  const halfWidthRef = useRef(0);
  const [paused, setPaused] = useState(false);
  const resumeTimerRef = useRef(null);

  // Measure the width of one PARTNERS pass so we can wrap seamlessly
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => {
      halfWidthRef.current = el.scrollWidth / 3;
      // Start in the middle copy so users can scroll left immediately
      el.scrollLeft = halfWidthRef.current;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-scroll loop
  useEffect(() => {
    if (paused) return;
    const el = trackRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const half = halfWidthRef.current;
      if (!half) return;
      el.scrollLeft += AUTO_SCROLL_PX_PER_TICK;
      // Seamless wrap — when we cross into the third copy, jump back into the middle copy
      if (el.scrollLeft >= half * 2) el.scrollLeft -= half;
    }, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  const pauseTemporarily = () => {
    setPaused(true);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setPaused(false), RESUME_AFTER_MS);
  };

  const scrollBy = (delta) => {
    const el = trackRef.current;
    if (!el) return;
    pauseTemporarily();
    el.scrollBy({ left: delta, behavior: "smooth" });
    // After the smooth scroll completes, normalise back into the middle copy if needed
    setTimeout(() => {
      const half = halfWidthRef.current;
      if (!half || !trackRef.current) return;
      if (trackRef.current.scrollLeft >= half * 2) trackRef.current.scrollLeft -= half;
      if (trackRef.current.scrollLeft < half * 0.5) trackRef.current.scrollLeft += half;
    }, 600);
  };

  return (
    <section
      className="py-20 border-y border-black/5 bg-paper-warm overflow-hidden"
      data-testid="partners-section"
    >
      <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12 mb-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-3">
              [ Partners & clients ]
            </div>
            <h2 className="font-display font-bold text-[1.75rem] sm:text-[2.25rem] tracking-tightest leading-tight">
              {heading}
            </h2>
          </div>
          <p className="text-[14px] text-muted-foreground max-w-md">
            We’re proud to deliver clean-air services for universities, schools, hotels,
            restaurants and operators across West London and beyond.
          </p>
        </div>
      </div>

      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-paper-cool to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-paper-cool to-transparent z-10 pointer-events-none" />

        {/* Prev arrow */}
        <button
          type="button"
          onClick={() => scrollBy(-ARROW_STEP_PX)}
          aria-label="Previous partner logos"
          data-testid="partners-prev"
          className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/90 hover:bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center transition hover:scale-105"
        >
          <ChevronLeft className="h-5 w-5 text-ink" strokeWidth={2.25} />
        </button>

        {/* Next arrow */}
        <button
          type="button"
          onClick={() => scrollBy(ARROW_STEP_PX)}
          aria-label="Next partner logos"
          data-testid="partners-next"
          className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/90 hover:bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center transition hover:scale-105"
        >
          <ChevronRight className="h-5 w-5 text-ink" strokeWidth={2.25} />
        </button>

        <div
          ref={trackRef}
          className="flex items-center overflow-x-auto scroll-smooth no-scrollbar"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((p, i) => (
            <PartnerItem key={`${p.name}-${i}`} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnersMarquee;
